import { type InputHTMLAttributes, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

type PropsInput = InputHTMLAttributes<HTMLInputElement>;
type PropsTextarea = TextareaHTMLAttributes<HTMLTextAreaElement>;
type PropsSelect = SelectHTMLAttributes<HTMLSelectElement>;

/**
 * Clases compartidas por `Input`, `Textarea` y `Select`: son los tres controles que el `.d.ts` del
 * cliente (`components/forms/Field.d.ts`, prop `as: 'input' | 'textarea' | 'select'`) trata como un
 * único componente con el mismo aspecto. Aquí se mantienen como tres componentes separados (cada
 * uno con su propio tipo de atributos HTML) para no cambiar la API actual de `Input`.
 *
 * Fondo `paper`, borde `border`, radio 8 px (`rounded-sm`), texto `ink` a 16 px (el CSS del cliente
 * usa 13 px — `--text-sm` — pero el cuerpo mínimo legible de esta oleada, ya aplicado antes de esta
 * tarea, es 16 px: evita el zoom automático de Safari/Chrome en móvil al enfocar el campo), placeholder
 * `gray` (única lectura permitida de ese token: es decoración, no texto que haya que leer).
 *
 * Foco (P-53, respuesta del cliente 17/09/2026: «El contorno oscuro, también en los campos y
 * desplegables [...] Aquí manda que se vea») = borde `accent` + el mismo contorno sólido de tinta
 * que ya usan botones y menús (`focus-visible:outline-2 focus-visible:outline-offset-2
 * focus-visible:outline-ink`, sobre `--color-ink`/`--da-ink` — copiado literal de `BASE` en
 * `button.tsx`, H-04). Ya NO es el halo `focus-ring` al 20% (`ring-3`/`ring-focus-ring`): se
 * distinguía poco sobre estos controles — era el punto 4 de las doce decisiones enviadas al
 * cliente el 6 de septiembre, contestado ahora. El token `--focus-ring` no se borra: sigue en
 * `tokens.css` para sus otros usos (`ArrowButton`, `chip.tsx`), solo deja de aplicarse aquí.
 *
 * `aria-invalid` con `border-error` (P-52/H-20: token de error de verdad, `--da-error` vía el alias
 * `--color-error` de `tokens.css`/`index.css`), no el `border-red-600` genérico que llevaba antes
 * este componente — el color VIVO de un estado va en el borde/aro (mismo criterio que documenta
 * `tokens.css` para el resto del sistema); el texto de error, aparte, siempre va en la tinta
 * `error-ink`, no en este fichero.
 */
/**
 * Exportada (H-21) para que `LanguageSwitcher`/`DecoradoFranjaHoraria` compartan el mismo
 * borde/radio/foco/transición en vez de duplicarlos a mano: ninguno de los dos consume esta
 * constante entera (necesitan su propio ancho/relleno/color por breakpoint, incompatibles con el
 * `w-full`/`px-3`/`text-base`/`text-ink` de aquí), así que cada uno filtra esas claves antes de
 * añadir las suyas — mismo motivo que documenta `docs/design-system.md` § La regla del fondo
 * oscuro para no dejar dos utilidades de una misma propiedad compitiendo por concatenación. Su
 * exportación no cambia el aspecto ni el comportamiento de `Input`/`Textarea`/`Select`.
 */
export const CLASE_BASE =
	'aria-invalid:border-error w-full rounded-sm border border-border bg-paper px-3 py-2 text-base text-ink placeholder:text-gray motion-safe:transition-colors motion-safe:duration-fast focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:opacity-50';

/**
 * Campo de texto base del sistema de diseño. Sin variantes ni texto propio: la etiqueta,
 * el mensaje de error y el texto de ayuda los aporta quien lo usa, normalmente a través de
 * `Field`. La altura mínima y el tamaño de letra cumplen el área táctil (44 × 44 px) y el
 * cuerpo mínimo (16 px) exigidos por accesibilidad.
 *
 * `autoComplete='off'` por defecto, ANTES del spread `{...props}` (igual que `type='text'`, justo
 * debajo): decisión explícita del usuario (2026-09-15) para que ningún campo del sistema ofrezca
 * sugerencias del navegador salvo que quien lo use pida lo contrario a propósito. Al ir antes del
 * spread, un consumidor que pase su propio `autoComplete` sigue ganando — el valor por defecto no
 * es una jaula. Matiz real que conviene dejar escrito, no un descuido: Chrome y Safari ignoran en
 * buena medida `autocomplete="off"` en los campos de contraseña y pueden seguir ofreciendo
 * credenciales guardadas, así que el efecto en esos campos es parcial, nunca una garantía — y
 * desactivarlo también inhabilita los gestores de contraseñas para ese campo. Es el trueque que
 * pidió el usuario, sabiendo lo anterior.
 */
export const Input = ({ className = '', ...props }: PropsInput): React.JSX.Element => (
	<input autoComplete='off' className={`${CLASE_BASE} min-h-11 ${className}`} type='text' {...props} />
);

/**
 * Área de texto multilínea con el mismo aspecto que `Input`, portada desde `components/forms/Field.d.ts`
 * (`as='textarea'`) del design system del cliente. Altura mínima 74 px (la del CSS de referencia,
 * ya por encima del área táctil de 44 px) y redimensionable solo en vertical, como en el original.
 *
 * `autoComplete='off'` por defecto, antes del spread `{...props}` — mismo motivo y el mismo matiz
 * de Chrome/Safari con campos de contraseña que documenta `Input`, arriba (decisión del usuario,
 * 2026-09-15): un consumidor que pase su propio `autoComplete` lo pisa igual.
 */
export const Textarea = ({ className = '', ...props }: PropsTextarea): React.JSX.Element => (
	<textarea autoComplete='off' className={`${CLASE_BASE} min-h-[74px] resize-y ${className}`} {...props} />
);

/**
 * Desplegable con el mismo aspecto que `Input`, portado desde `components/forms/Field.d.ts`
 * (`as='select'`) del design system del cliente. El CSS de referencia sustituye la flecha nativa
 * por un SVG de fondo en un `data:` URI; se omite aquí (no hay utilidad de Tailwind razonable para
 * incrustar un `data:` URI largo como valor arbitrario sin fragilidad) y se deja la flecha nativa
 * del navegador, que ya es accesible y perceptible.
 *
 * Sin `autoComplete='off'` a propósito, a diferencia de `Input`/`Textarea` (2026-09-15): un
 * `<select>` no ofrece una lista de sugerencias de autocompletado del navegador como sí hacen los
 * campos de texto, así que el atributo sería ruido sin ningún efecto observable.
 */
export const Select = ({ children, className = '', ...props }: PropsSelect): React.JSX.Element => (
	<select className={`${CLASE_BASE} min-h-11 ${className}`} {...props}>
		{children}
	</select>
);
